// src/screens/DrawdownAnalysis.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Phase 6 — Drawdown / Retirement Income Analysis.
// Three drawdown rates (3%/4%/5%), inflation impact, phase 1/2/3 income,
// DB pension + rental income integration.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ClientData } from '../types/ClientData';
import { formatCurrency, formatCurrencyK } from '../utils/format';
import { PLSA } from '../config/plsaConfig';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

const fmtK = formatCurrencyK;

const INFLATION = 0.025;

// Three income phases in drawdown:
// Phase 1: Freedom age → State pension age (funding gap fully from pot + any DB/rental)
// Phase 2: State pension age → age 75 (state pension kicks in, draw less from pot)
// Phase 3: Age 75+ (potentially DB pension + state pension + reduced drawdown)

interface DrawdownYear {
  age: number;
  phase: 1 | 2 | 3;
  statePension: number;
  dbPension: number;
  rentalIncome: number;
  drawdown3: number;
  drawdown4: number;
  drawdown5: number;
  pot3: number;
  pot4: number;
  pot5: number;
  totalIncome3: number;
  totalIncome4: number;
  totalIncome5: number;
}

function projectDrawdown(data: ClientData): DrawdownYear[] {
  const freedomAge = data.targetFreedomAge || 60;
  const spAge = data.statePensionAge || 67;
  const dbStartAge = data.hasDbPension ? (data.dbPensionStartAge || 67) : 999;
  const years: DrawdownYear[] = [];

  // Estimate pot at freedom age — uses Assumptions growth rates
  const yearsToFreedom = Math.max(0, freedomAge - (data.clientAge || 40));
  const pensionGrowth = (data.assumptionPensionGrowth || 0.08);
  const isaGrowth = (data.assumptionIsaGrowth || 0.08);
  const pensionMonthly = (data.pensionCompanyFunded || 0) + (data.pensionMonthly || 0);
  const isaMonthly = data.isaContribution || 0;
  let pensionBal = data.pensionBalance || 0;
  let isaBal = data.investmentsBalance || 0;
  for (let i = 0; i < yearsToFreedom; i++) {
    pensionBal = pensionBal * (1 + pensionGrowth) + pensionMonthly * 12;
    isaBal = isaBal * (1 + isaGrowth) + isaMonthly * 12;
  }
  const totalPotAtFreedom = pensionBal + isaBal;

  // Annual income needed (from PLSA or freedomNumber calc)
  const annualNeed = data.annualRetirementIncomeNeeded || (data.isCouple ? PLSA.couple.moderate : PLSA.single.moderate);
  const spAnnual = data.statePensionAnnual || 11500;
  const dbAnnual = data.hasDbPension ? (data.dbPensionAnnualIncome || 0) : 0;
  const rentalAnnual = data.rentalIncomeNet || 0;

  let pot3 = totalPotAtFreedom;
  let pot4 = totalPotAtFreedom;
  let pot5 = totalPotAtFreedom;

  for (let age = freedomAge; age <= 95; age++) {
    const yearsFromFreedom = age - freedomAge;
    const inflatedNeed = annualNeed * Math.pow(1 + INFLATION, yearsFromFreedom);
    const phase: 1 | 2 | 3 = age < spAge ? 1 : age < 75 ? 2 : 3;

    // Income sources available this year
    const sp = age >= spAge ? spAnnual * Math.pow(1 + INFLATION, yearsFromFreedom) : 0; // triple-locked approx
    const db = age >= dbStartAge ? dbAnnual * (data.dbPensionIndexLinked ? Math.pow(1 + INFLATION, yearsFromFreedom) : 1) : 0;
    const rental = rentalAnnual * Math.pow(1 + INFLATION, yearsFromFreedom * 0.5); // modest rental growth

    // Gap to fill from pot
    const gapFromPot = Math.max(0, inflatedNeed - sp - db - rental);

    // Drawdown at 3%/4%/5%
    const d3 = Math.min(pot3 * 0.03, gapFromPot);
    const d4 = Math.min(pot4 * 0.04, gapFromPot);
    const d5 = Math.min(pot5 * 0.05, gapFromPot);

    years.push({
      age, phase,
      statePension: Math.round(sp),
      dbPension: Math.round(db),
      rentalIncome: Math.round(rental),
      drawdown3: Math.round(d3),
      drawdown4: Math.round(d4),
      drawdown5: Math.round(d5),
      pot3: Math.round(pot3),
      pot4: Math.round(pot4),
      pot5: Math.round(pot5),
      totalIncome3: Math.round(sp + db + rental + d3),
      totalIncome4: Math.round(sp + db + rental + d4),
      totalIncome5: Math.round(sp + db + rental + d5),
    });

    // Remaining pot grows at 4% (nominal), minus drawdown
    pot3 = Math.max(0, (pot3 - d3) * 1.04);
    pot4 = Math.max(0, (pot4 - d4) * 1.04);
    pot5 = Math.max(0, (pot5 - d5) * 1.04);
  }

  return years;
}

function SummaryCard({ label, value, colour, sub }: { label: string; value: string; colour?: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${colour || 'text-ma-navy'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function DrawdownAnalysis({ data, onUpdate }: Props) {
  const years = useMemo(() => projectDrawdown(data), [data]);
  const freedomAge = data.targetFreedomAge || 60;
  const spAge = data.statePensionAge || 67;
  const pensionGrowth = data.assumptionPensionGrowth || 0.08;
  const isaGrowth = data.assumptionIsaGrowth || 0.08;

  // Pot depletion ages
  const depletes3 = years.find(y => y.pot3 <= 0)?.age || null;
  const depletes4 = years.find(y => y.pot4 <= 0)?.age || null;
  const depletes5 = years.find(y => y.pot5 <= 0)?.age || null;

  const potAtFreedom = years[0]?.pot3 || 0;

  // Chart: pot remaining at each rate
  const chartData = useMemo(() => ({
    labels: years.map(y => `${y.age}`),
    datasets: [
      { label: '3% drawdown', data: years.map(y => y.pot3), borderColor: '#34D399', backgroundColor: '#34D39920', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0 },
      { label: '4% drawdown', data: years.map(y => y.pot4), borderColor: '#64DAD2', backgroundColor: '#64DAD220', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0 },
      { label: '5% drawdown', data: years.map(y => y.pot5), borderColor: '#F87171', backgroundColor: '#F8717120', borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0 },
    ],
  }), [years]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'circle' as const, padding: 16, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 20, callback: function(this: any, _v: any, i: number) { return i % 5 === 0 ? years[i]?.age : ''; } } },
      y: { grid: { color: '#f3f4f6' }, ticks: { callback: (v: any) => fmtK(v), font: { size: 10 } }, beginAtZero: true },
    },
  }), [years]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 mb-6">
        <h2 className="text-lg font-bold text-ma-navy mb-1">Drawdown / Retirement Income Analysis</h2>
        <p className="text-sm text-indigo-600">
          How long your pot lasts at different drawdown rates, and where your income comes from in each phase.
        </p>
      </div>

      {/* Pot at freedom */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Projected pot at age {freedomAge}</div>
            <div className="text-2xl font-bold text-ma-navy">{fmt(potAtFreedom)}</div>
          </div>
          <div className="text-xs text-gray-400 text-right">
            Pension at {(pensionGrowth * 100).toFixed(0)}%, ISA at {(isaGrowth * 100).toFixed(0)}%<br />
            from Assumptions
          </div>
        </div>
      </div>

      {/* Income at each drawdown rate */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {([
          { rate: 3, colour: 'green', depletes: depletes3, annual: potAtFreedom * 0.03 },
          { rate: 4, colour: 'blue', depletes: depletes4, annual: potAtFreedom * 0.04 },
          { rate: 5, colour: 'red', depletes: depletes5, annual: potAtFreedom * 0.05 },
        ] as const).map(d => {
          const monthly = d.annual / 12;
          return (
            <div key={d.rate} className={`bg-${d.colour === 'blue' ? 'blue' : d.colour}-50 border border-${d.colour === 'blue' ? 'blue' : d.colour}-200 rounded-2xl p-5`}>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{d.rate}% drawdown</div>
              <div className={`text-2xl font-bold ${d.colour === 'green' ? 'text-green-700' : d.colour === 'blue' ? 'text-ma-navy' : 'text-red-700'}`}>
                {fmt(Math.round(monthly))}<span className="text-sm font-medium">/mo</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">{fmt(Math.round(d.annual))}/year</div>
              <div className={`text-xs font-medium mt-2 ${d.depletes ? 'text-red-600' : 'text-green-600'}`}>
                {d.depletes ? `Pot depletes at age ${d.depletes}` : 'Lasts to age 95+'}
              </div>
            </div>
          );
        })}
      </div>

      {/* PLSA reference */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-6">
        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Pensions and Lifetime Savings Association (PLSA) Living Standards Reference{data.isCouple ? ' (couple)' : ' (individual)'}</div>
        <div className="grid grid-cols-3 gap-4 text-center text-xs">
          <div>
            <div className="text-gray-400">Comfortable</div>
            <div className="font-bold text-gray-700">{fmt(Math.round((data.isCouple ? PLSA.couple.comfortable : PLSA.single.comfortable) / 12))}/mo</div>
            <div className="text-gray-400">{fmt(data.isCouple ? PLSA.couple.comfortable : PLSA.single.comfortable)}/yr</div>
          </div>
          <div>
            <div className="text-gray-400">Moderate</div>
            <div className="font-bold text-gray-700">{fmt(Math.round((data.isCouple ? PLSA.couple.moderate : PLSA.single.moderate) / 12))}/mo</div>
            <div className="text-gray-400">{fmt(data.isCouple ? PLSA.couple.moderate : PLSA.single.moderate)}/yr</div>
          </div>
          <div>
            <div className="text-gray-400">Minimum</div>
            <div className="font-bold text-gray-700">{fmt(Math.round((data.isCouple ? PLSA.couple.minimum : PLSA.single.minimum) / 12))}/mo</div>
            <div className="text-gray-400">{fmt(data.isCouple ? PLSA.couple.minimum : PLSA.single.minimum)}/yr</div>
          </div>
        </div>
      </div>

      {/* Pot depletion chart */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6" style={{ height: 380 }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Income phases */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-blue-800 mb-2">Phase 1: Ages {freedomAge}–{spAge - 1}</h3>
          <p className="text-xs text-blue-600 mb-3">Before state pension — pot funds most of your income.</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Pot drawdown (4%)</span>
              <span className="font-medium">{fmt(years[0]?.drawdown4 || 0)}/yr</span>
            </div>
            {(data.rentalIncomeNet || 0) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Rental income</span>
                <span className="font-medium">{fmt(years[0]?.rentalIncome || 0)}/yr</span>
              </div>
            )}
            {data.hasDbPension && (data.dbPensionStartAge || 67) <= freedomAge && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">DB pension</span>
                <span className="font-medium">{fmt(years[0]?.dbPension || 0)}/yr</span>
              </div>
            )}
            <div className="border-t border-blue-200 pt-1 flex justify-between text-xs font-bold text-blue-800">
              <span>Total</span>
              <span>{fmt(years[0]?.totalIncome4 || 0)}/yr</span>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-green-800 mb-2">Phase 2: Ages {spAge}–74</h3>
          <p className="text-xs text-green-600 mb-3">State pension starts — draw less from pot.</p>
          {(() => {
            const phase2Start = years.find(y => y.age === spAge);
            if (!phase2Start) return null;
            return (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">State pension</span>
                  <span className="font-medium">{fmt(phase2Start.statePension)}/yr</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Pot drawdown (4%)</span>
                  <span className="font-medium">{fmt(phase2Start.drawdown4)}/yr</span>
                </div>
                {phase2Start.dbPension > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">DB pension</span>
                    <span className="font-medium">{fmt(phase2Start.dbPension)}/yr</span>
                  </div>
                )}
                {phase2Start.rentalIncome > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Rental income</span>
                    <span className="font-medium">{fmt(phase2Start.rentalIncome)}/yr</span>
                  </div>
                )}
                <div className="border-t border-green-200 pt-1 flex justify-between text-xs font-bold text-green-800">
                  <span>Total</span>
                  <span>{fmt(phase2Start.totalIncome4)}/yr</span>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-purple-800 mb-2">Phase 3: Age 75+</h3>
          <p className="text-xs text-purple-600 mb-3">All guaranteed income sources active — reduced pot drawdown.</p>
          {(() => {
            const phase3Start = years.find(y => y.age === 75);
            if (!phase3Start) return null;
            return (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">State pension</span>
                  <span className="font-medium">{fmt(phase3Start.statePension)}/yr</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Pot drawdown (4%)</span>
                  <span className="font-medium">{fmt(phase3Start.drawdown4)}/yr</span>
                </div>
                {phase3Start.dbPension > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">DB pension</span>
                    <span className="font-medium">{fmt(phase3Start.dbPension)}/yr</span>
                  </div>
                )}
                {phase3Start.rentalIncome > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Rental income</span>
                    <span className="font-medium">{fmt(phase3Start.rentalIncome)}/yr</span>
                  </div>
                )}
                <div className="border-t border-purple-200 pt-1 flex justify-between text-xs font-bold text-purple-800">
                  <span>Total</span>
                  <span>{fmt(phase3Start.totalIncome4)}/yr</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">Remaining pot: {fmt(phase3Start.pot4)}</div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Inflation impact */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
        <h3 className="text-sm font-bold text-amber-800 mb-2">Inflation Impact</h3>
        <p className="text-xs text-amber-600 mb-3">
          At 2.5% inflation, your purchasing power reduces significantly over time.
        </p>
        <div className="grid grid-cols-3 gap-4">
          {[10, 20, 30].map(yrs => {
            const inflatedNeed = (data.annualRetirementIncomeNeeded || (data.isCouple ? PLSA.couple.moderate : PLSA.single.moderate)) * Math.pow(1 + INFLATION, yrs);
            return (
              <div key={yrs} className="text-center">
                <div className="text-xs text-gray-500">In {yrs} years</div>
                <div className="text-sm font-bold text-amber-800">{fmt(inflatedNeed)}/yr</div>
                <div className="text-xs text-gray-400">needed for same lifestyle</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Assumptions */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-xs text-gray-400">
        <strong className="text-gray-500">Assumptions:</strong> Pot grows at 4% nominal during drawdown. Inflation at 2.5%.
        State pension triple-locked (approximated as 2.5%). Drawdown rates are sustainable withdrawal rates (3%/4%/5%).
        This is an illustration only — actual returns will vary.
      </div>
    </div>
  );
}
